
import { useToast } from "@/hooks/use-toast";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

interface UseExportActionsProps {
  onAuthRequired: () => void;
  isAuthenticated: boolean;
}

export const useExportActions = ({ onAuthRequired, isAuthenticated }: UseExportActionsProps) => {
  const { toast } = useToast();

  const exportToImage = async (format: 'png' | 'jpg') => {
    if (!isAuthenticated) {
      onAuthRequired();
      return;
    }

    const element = document.getElementById('floor-plan-container');
    if (!element) {
      throw new Error('Kan het canvas element niet vinden');
    }

    // Fit the view to ensure all elements are visible
    const flowInstance = (element as any).__reactFlowInstance;
    if (flowInstance) {
      flowInstance.fitView({ padding: 0.2, duration: 0 });
      // Wait for the view to adjust
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // Get the react-flow container for better targeting
    const flowContainer = element.querySelector('.react-flow__viewport') || element;
    
    const canvas = await html2canvas(flowContainer as HTMLElement, {
      backgroundColor: '#FAFAFA',
      scale: 2, // Higher scale for better quality
      useCORS: true,
      allowTaint: true,
      logging: false,
      // Ignore UI controls that aren't part of the floor plan
      ignoreElements: (el) => {
        return el.classList?.contains('react-flow__controls') ||
               el.classList?.contains('react-flow__minimap') ||
               el.classList?.contains('react-flow__panel') ||
               el.classList?.contains('react-flow__handle');
      }
    });
    
    const dataUrl = canvas.toDataURL(`image/${format}`);
    const link = document.createElement('a');
    link.download = `plattegrond.${format}`;
    link.href = dataUrl;
    link.click();

    toast({
      title: "Export succesvol",
      description: `Plattegrond is geëxporteerd als ${format.toUpperCase()}`,
    });
  };

  const exportToPDF = async () => {
    if (!isAuthenticated) {
      onAuthRequired();
      return;
    }

    const element = document.getElementById('floor-plan-container');
    if (!element) {
      throw new Error('Kan het canvas element niet vinden');
    }

    // Fit the view to ensure all elements are visible
    const flowInstance = (element as any).__reactFlowInstance;
    if (flowInstance) {
      flowInstance.fitView({ padding: 0.1, duration: 0 });
      // Wait for the view to adjust
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // Get the react-flow container for better targeting
    const flowContainer = element.querySelector('.react-flow__viewport') || element;
    
    const canvas = await html2canvas(flowContainer as HTMLElement, {
      backgroundColor: '#FAFAFA',
      scale: 2, // Higher scale for better quality
      useCORS: true,
      allowTaint: true,
      logging: false,
      // Ignore UI controls that aren't part of the floor plan
      ignoreElements: (el) => {
        return el.classList?.contains('react-flow__controls') ||
               el.classList?.contains('react-flow__minimap') ||
               el.classList?.contains('react-flow__panel') ||
               el.classList?.contains('react-flow__handle');
      }
    });
    
    // Create PDF with appropriate dimensions
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
      unit: 'px',
      format: [canvas.width, canvas.height]
    });
    
    // Calculate proper PDF dimensions to fit the content
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const ratio = Math.min(pdfWidth / canvas.width, pdfHeight / canvas.height);
    const canvasWidth = canvas.width * ratio;
    const canvasHeight = canvas.height * ratio;
    const marginX = (pdfWidth - canvasWidth) / 2;
    const marginY = (pdfHeight - canvasHeight) / 2;
    
    pdf.addImage(imgData, 'PNG', marginX, marginY, canvasWidth, canvasHeight);
    pdf.save('plattegrond.pdf');

    toast({
      title: "Export succesvol",
      description: "Plattegrond is geëxporteerd als PDF",
    });
  };

  const exportToJSON = async () => {
    if (!isAuthenticated) {
      onAuthRequired();
      return;
    }

    const editorElement = document.getElementById('floor-plan-container');
    if (!editorElement) {
      throw new Error('Kan het canvas element niet vinden');
    }

    // Haal de nodes en edges op uit de ReactFlow instance
    const flowInstance = (editorElement as any).__reactFlowInstance;
    if (!flowInstance) {
      // Als we de instance niet kunnen ophalen, gebruiken we een andere methode
      // Dit is een fallback voor het geval de ReactFlow API verandert
      const editorState = {
        nodes: window.localStorage.getItem('floor-plan-editor-state') 
          ? JSON.parse(window.localStorage.getItem('floor-plan-editor-state') || '{}').nodes || []
          : [],
        edges: window.localStorage.getItem('floor-plan-editor-state')
          ? JSON.parse(window.localStorage.getItem('floor-plan-editor-state') || '{}').edges || []
          : []
      };

      const jsonString = JSON.stringify(editorState, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.download = 'plattegrond.json';
      link.href = url;
      link.click();
      
      toast({
        title: "Export succesvol",
        description: "Plattegrond is geëxporteerd als JSON",
      });
      return;
    }

    // Als we wel de flowInstance hebben, gebruik die data
    const editorState = {
      nodes: flowInstance.getNodes() || [],
      edges: flowInstance.getEdges() || []
    };

    const jsonString = JSON.stringify(editorState, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.download = 'plattegrond.json';
    link.href = url;
    link.click();
    
    toast({
      title: "Export succesvol",
      description: "Plattegrond is geëxporteerd als JSON",
    });
  };

  return {
    exportToImage,
    exportToPDF,
    exportToJSON,
  };
};
